import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { emailAPI, dossiersAPI } from '../api/api';
import { toast } from '../hooks/use-toast';
import AppLayout from '../components/AppLayout';
import EmailComposer from '../components/EmailComposer';

const fmtDate = (d) => {
  if (!d) return '—';
  const date = new Date(d);
  return date.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
};

const STATUS = {
  sent: { label: 'Envoyé', color: '#34D399', bg: 'rgba(52,211,153,0.12)' },
  sending: { label: 'En cours', color: '#FBBF24', bg: 'rgba(251,191,36,0.12)' },
  pending: { label: 'En attente', color: '#FBBF24', bg: 'rgba(251,191,36,0.12)' },
  failed: { label: 'Échec', color: '#F87171', bg: 'rgba(248,113,113,0.12)' },
};

const SentEmails = () => {
  const { user, currentOrg, orgLoading, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [messages, setMessages] = useState([]);
  const [dossiers, setDossiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [composerOpen, setComposerOpen] = useState(false);

  useEffect(() => {
    if (!authLoading && !user) navigate('/login', { replace: true });
  }, [user, authLoading, navigate]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [msgs, dos] = await Promise.all([emailAPI.list(), dossiersAPI.getAll().catch(() => [])]);
      setMessages(msgs);
      setDossiers(dos);
    } catch {
      toast({ title: 'Erreur', description: 'Impossible de charger les emails.', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (currentOrg) fetchData();
    else if (!authLoading) setLoading(false);
  }, [currentOrg, authLoading, fetchData]);

  if (authLoading || orgLoading) return null;

  return (
    <AppLayout title="Mails envoyés">
      <div className="sd-section-head">
        <div>
          <div className="sd-section-title">Mails envoyés</div>
          <div className="sd-section-sub">
            {loading ? '…' : `${messages.length} email${messages.length !== 1 ? 's' : ''}`}
          </div>
        </div>
        <button className="sd-btn sd-btn-primary" onClick={() => setComposerOpen(true)}>
          <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M14.5 1.5L7 9M14.5 1.5l-4.5 13-2.5-5.5L2 6z"/></svg>
          Nouvel email
        </button>
      </div>

      {loading ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {[1, 2, 3].map((i) => <div key={i} className="sd-skeleton" style={{ height: 56, borderRadius: 10 }} />)}
        </div>
      ) : messages.length === 0 ? (
        <div className="sd-lib-empty">
          <div className="sd-lib-empty-icon">
            <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="var(--vio2)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="4" width="20" height="16" rx="3"/><path d="M3 6l9 7 9-7"/></svg>
          </div>
          <div className="sd-lib-empty-title">Aucun email envoyé</div>
          <div className="sd-lib-empty-sub">Envoyez des consultants à un client directement depuis Skilldeck.</div>
          <button className="sd-btn sd-btn-primary" onClick={() => setComposerOpen(true)} style={{ margin: '0 auto' }}>Composer un email</button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {messages.map((m) => {
            const st = STATUS[m.status] || STATUS.pending;
            const nAtt = Array.isArray(m.attachments) ? m.attachments.length : 0;
            return (
              <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '13px 16px', background: 'var(--panel,#16161d)', border: '1px solid var(--border)', borderRadius: 11 }}>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
                    <span style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--white)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {m.subject || '(sans objet)'}
                    </span>
                    <span style={{ fontSize: 10.5, fontWeight: 700, color: st.color, background: st.bg, padding: '2px 8px', borderRadius: 20, flexShrink: 0 }}>{st.label}</span>
                  </div>
                  <div style={{ fontSize: 11.5, color: 'var(--muted2)', display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                    <span>À&nbsp;<strong style={{ color: 'var(--muted2)', fontWeight: 600 }}>{(m.to_emails || []).join(', ') || '—'}</strong></span>
                    {m.client_name && <span>· Client&nbsp;<strong style={{ color: 'var(--vio2)', fontWeight: 600 }}>{m.client_name}</strong></span>}
                    {nAtt > 0 && (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                        <svg width="10" height="10" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.4"><path d="M3 1.5h4L9.5 4v6.5a1 1 0 01-1 1h-5a1 1 0 01-1-1v-8a1 1 0 011-1z"/></svg>
                        {nAtt} dossier{nAtt !== 1 ? 's' : ''}
                      </span>
                    )}
                  </div>
                </div>
                <div style={{ fontSize: 11, color: 'var(--muted)', flexShrink: 0 }}>{fmtDate(m.sent_at || m.created_at)}</div>
              </div>
            );
          })}
        </div>
      )}

      <EmailComposer
        open={composerOpen}
        onClose={() => setComposerOpen(false)}
        onSent={() => fetchData()}
        availableDossiers={dossiers}
      />
    </AppLayout>
  );
};

export default SentEmails;
